import { useRef, useState } from 'react';
import { Play, Pause, X, RotateCcw, ChevronDown, RefreshCw, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import type { TimerState } from '@pinepomo/core';

export interface ExternalTask {
  id: string;
  content: string;
  source: 'todoist' | 'notion';
  priority?: number;
  area?: string;
  status?: string;
}

type TimerStore = {
  (): TimerState;
  getState: () => TimerState;
};

interface TimerControlsProps {
  store: TimerStore;
  tasks: ExternalTask[];
  isTasksLoading: boolean;
  onTasksRefresh: () => void;
  className?: string;
}

const SOURCE_COLORS: Record<string, string> = {
  todoist: '#E44332',
  notion: '#000000',
};

const SOURCE_LABELS: Record<string, string> = {
  todoist: 'Todoist',
  notion: 'Notion',
};

export function TimerControls({
  store,
  tasks,
  isTasksLoading,
  onTasksRefresh,
  className,
}: TimerControlsProps) {
  const { session, start, pause, resume, cancel, reset } = store();
  const [taskName, setTaskName] = useState('');
  const [selectedTaskKey, setSelectedTaskKey] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const status = session?.status ?? 'idle';
  const hasTasks = tasks.length > 0;

  const taskKey = (t: ExternalTask) => `${t.source}:${t.id}`;
  const selectedTask = tasks.find((t) => taskKey(t) === selectedTaskKey);

  const handleStart = () => {
    const name = isManualEntry ? taskName.trim() : selectedTask?.content;
    start({
      taskName: name || undefined,
      todoistTaskId:
        !isManualEntry && selectedTask?.source === 'todoist' ? selectedTask.id : undefined,
      notionTaskId:
        !isManualEntry && selectedTask?.source === 'notion' ? selectedTask.id : undefined,
    });
    setTaskName('');
    setSelectedTaskKey(null);
    setIsManualEntry(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleStart();
    }
  };

  const handleSelectTask = (task: ExternalTask) => {
    setSelectedTaskKey(taskKey(task));
    setTaskName(task.content);
    setIsDropdownOpen(false);
    setIsManualEntry(false);
  };

  const handleManualEntry = () => {
    setIsManualEntry(true);
    setSelectedTaskKey(null);
    setTaskName('');
    setIsDropdownOpen(false);
  };

  const displayValue = isManualEntry ? taskName : selectedTask?.content || '';

  return (
    <div className={cn('flex flex-col items-center gap-4 w-full max-w-sm', className)}>
      {/* Task selector - shown when idle */}
      {status === 'idle' && (
        <div className="w-full relative" ref={dropdownRef}>
          {isManualEntry ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={taskName}
                onChange={(e) => setTaskName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="What are you working on?"
                className={cn(
                  'flex-1 px-4 py-3 text-center',
                  'rounded-xl',
                  'bg-card border border-muted',
                  'text-main placeholder:text-muted',
                  'focus:outline-none focus:border-primary-600/50'
                )}
                autoFocus
              />
              {hasTasks && (
                <button
                  onClick={() => setIsManualEntry(false)}
                  className={cn(
                    'px-3 rounded-xl',
                    'bg-card border border-muted',
                    'text-secondary hover:text-main',
                    'transition-colors'
                  )}
                  title="Select from tasks"
                >
                  <ChevronDown className="w-5 h-5" />
                </button>
              )}
            </div>
          ) : hasTasks ? (
            <>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={cn(
                  'w-full px-4 py-3',
                  'rounded-xl',
                  'bg-card border border-muted',
                  'text-main',
                  'flex items-center justify-between gap-2',
                  'focus:outline-none focus:border-primary-600/50',
                  'transition-colors'
                )}
              >
                <span className={cn('truncate', !displayValue && 'text-muted')}>
                  {displayValue || 'Select a task...'}
                </span>
                <ChevronDown
                  className={cn(
                    'w-5 h-5 text-secondary shrink-0',
                    'transition-transform',
                    isDropdownOpen && 'rotate-180'
                  )}
                />
              </button>

              {isDropdownOpen && (
                <div
                  className={cn(
                    'absolute top-full left-0 right-0 mt-2 z-50',
                    'bg-card border border-muted rounded-xl',
                    'shadow-lg overflow-hidden',
                    'max-h-64 overflow-y-auto'
                  )}
                >
                  {/* Manual entry option */}
                  <button
                    onClick={handleManualEntry}
                    className={cn(
                      'w-full px-4 py-3 text-left',
                      'flex items-center gap-3',
                      'hover:bg-elevated transition-colors',
                      'border-b border-muted'
                    )}
                  >
                    <Pencil className="w-4 h-4 text-secondary" />
                    <span className="text-secondary">Manual entry...</span>
                  </button>

                  {/* Refresh button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onTasksRefresh();
                    }}
                    disabled={isTasksLoading}
                    className={cn(
                      'w-full px-4 py-2 text-left',
                      'flex items-center gap-3',
                      'hover:bg-elevated transition-colors',
                      'border-b border-muted text-xs text-muted'
                    )}
                  >
                    <RefreshCw className={cn('w-3 h-3', isTasksLoading && 'animate-spin')} />
                    <span>Refresh tasks</span>
                  </button>

                  {/* Task list */}
                  {tasks.map((task) => (
                    <button
                      key={`${task.source}-${task.id}`}
                      onClick={() => handleSelectTask(task)}
                      className={cn(
                        'w-full px-4 py-3 text-left',
                        'flex items-center gap-3',
                        'hover:bg-elevated transition-colors',
                        selectedTaskKey === taskKey(task) && 'bg-primary-500/10'
                      )}
                    >
                      {/* Source indicator dot */}
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: SOURCE_COLORS[task.source] || '#888' }}
                        title={SOURCE_LABELS[task.source] || task.source}
                      />
                      <span className="truncate text-main">{task.content}</span>
                      {/* Area badge */}
                      {task.area && (
                        <span className="text-xs px-1.5 py-0.5 rounded shrink-0 bg-purple-500/20 text-purple-400">
                          {task.area}
                        </span>
                      )}
                      {/* Priority badge (Todoist) */}
                      {task.priority && task.priority > 1 && (
                        <span
                          className={cn(
                            'text-xs px-1.5 py-0.5 rounded shrink-0',
                            task.priority === 4 && 'bg-red-500/20 text-red-400',
                            task.priority === 3 && 'bg-orange-500/20 text-orange-400',
                            task.priority === 2 && 'bg-blue-500/20 text-blue-400'
                          )}
                        >
                          P{5 - task.priority}
                        </span>
                      )}
                      {/* Status badge (Notion) */}
                      {task.status && task.source === 'notion' && (
                        <span
                          className={cn(
                            'text-xs px-1.5 py-0.5 rounded shrink-0',
                            task.status === 'In progress'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-zinc-500/20 text-zinc-400'
                          )}
                        >
                          {task.status}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <input
              type="text"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="What are you working on?"
              className={cn(
                'w-full px-4 py-3 text-center',
                'rounded-xl',
                'bg-card border border-muted',
                'text-main placeholder:text-muted',
                'focus:outline-none focus:border-primary-600/50'
              )}
            />
          )}
        </div>
      )}

      {/* Control buttons */}
      <div className="flex items-center gap-3">
        {status === 'idle' && (
          <Button
            variant="primary"
            size="lg"
            onClick={handleStart}
            icon={<Play className="w-5 h-5" />}
          >
            Start Focus
          </Button>
        )}

        {status === 'running' && (
          <>
            <Button
              variant="secondary"
              size="lg"
              onClick={pause}
              icon={<Pause className="w-5 h-5" />}
            >
              Pause
            </Button>
            <Button variant="ghost" size="md" onClick={cancel} icon={<X className="w-5 h-5" />}>
              Cancel
            </Button>
          </>
        )}

        {status === 'paused' && (
          <>
            <Button
              variant="primary"
              size="lg"
              onClick={resume}
              icon={<Play className="w-5 h-5" />}
            >
              Resume
            </Button>
            <Button variant="ghost" size="md" onClick={cancel} icon={<X className="w-5 h-5" />}>
              Cancel
            </Button>
          </>
        )}

        {(status === 'completed' || status === 'cancelled') && (
          <Button
            variant="primary"
            size="lg"
            onClick={reset}
            icon={<RotateCcw className="w-5 h-5" />}
          >
            New Focus
          </Button>
        )}
      </div>
    </div>
  );
}
